import { Link } from "react-router-dom";
import type { Business } from "@/types/directory";
import Badge from "@/components/base/Badge";
import RatingStars from "@/components/base/RatingStars";
import SafeImage from "@/components/base/SafeImage";
import SocialShare from "@/components/feature/SocialShare";
import ShareCounter from "@/components/feature/ShareCounter";
import { trackShare } from "@/services/shareService";
import { computeOpenNow, getHoursHint } from "@/utils/hours";
import { getPlanLabel } from "@/utils/plans";
import { getBusinessFallbackImage } from "@/utils/fallbackImage";

interface BusinessHeroProps {
  business: Business;
}

export default function BusinessHero({ business }: BusinessHeroProps) {
  const fallbackImage = getBusinessFallbackImage(business);

  const initials = business.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const shortAddress = business.address.split(",").slice(-3).join(",").trim();
  const isOpenNow = computeOpenNow(business.hours, business.openNow);
  const hoursHint = getHoursHint(business.hours, isOpenNow);

  const planSlug = (business.plan ?? "").trim().toLowerCase();
  const planLabel = getPlanLabel(business.plan);
  const planBadge = planSlug === "premium"
    ? { tone: "accent" as const, icon: "ri-vip-crown-2-fill" }
    : planSlug === "business"
      ? { tone: "primary" as const, icon: "ri-rocket-2-fill" }
      : { tone: "neutral" as const, icon: "ri-store-3-line" };

  return (
    <section className="w-full">
      <div className="relative h-52 w-full overflow-hidden bg-primary-900 md:h-72">
        <SafeImage
          src={business.cover}
          fallback={fallbackImage}
          alt={`${business.name} - ${business.subcategory}`}
          title={`${business.name} - ${business.subcategory}`}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-foreground-950/10 to-transparent" />
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-6">
        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:mt-8 md:p-8">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link
              to={`/category/${business.categorySlug}`}
              className="transition-colors hover:text-primary-700"
            >
              {business.subcategory}
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">{business.name}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-5">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-primary-500 font-heading text-xl font-extrabold text-background-50 md:h-20 md:w-20 md:text-2xl">
                {initials}
              </span>
              <div>
                <h1 className="font-heading text-2xl font-extrabold leading-tight text-[#2C488E] md:text-3xl">
                  {business.name}
                </h1>
                <p className="mt-1.5 text-sm text-foreground-600 md:text-base">{business.tagline}</p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {business.sponsored ? <Badge tone="accent">Sponsored</Badge> : null}
                  {business.featured && !business.sponsored ? (
                    <Badge tone="primary">Featured</Badge>
                  ) : null}
                  {business.verified ? (
                    <Badge tone="secondary" icon="ri-verified-badge-fill">
                      Verified
                    </Badge>
                  ) : null}
                  <Badge tone={planBadge.tone} icon={planBadge.icon}>
                    {planLabel} Plan
                  </Badge>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                  <span className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                      <i className="ri-price-tag-3-line text-base" />
                    </span>
                    {business.subcategory}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                      <i className="ri-history-line text-base" />
                    </span>
                    {business.yearsInBusiness} years in business
                  </span>
                  {isOpenNow ? (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-secondary-700">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-secondary-500" />
                        Open now
                      </span>
                      {hoursHint ? (
                        <span className="font-normal text-foreground-500">{hoursHint}</span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-foreground-500">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-foreground-400" />
                        Currently closed
                      </span>
                      {hoursHint ? (
                        <span className="font-normal text-foreground-400">{hoursHint}</span>
                      ) : null}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 items-start lg:items-end">
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6 lg:justify-end">
                <div className="rounded-md border border-background-200 bg-background-100 px-5 py-3">
                  <RatingStars rating={business.rating} count={business.reviewCount} />
                  <p className="mt-1 text-xs text-foreground-500">Based on verified customer reviews</p>
                </div>
                <div className="flex flex-col items-start gap-2 lg:items-end">
                  <SocialShare
                    title={business.name}
                    onShare={(platform) => trackShare("business", business.slug, business.name, platform)}
                  />
                  <ShareCounter entityType="business" slug={business.slug} plan={business.plan} />
                </div>
              </div>

              <span className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="flex h-4 w-4 items-center justify-center text-red-500">
                  <i className="ri-map-pin-line text-base" />
                </span>
                {shortAddress}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}